import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { HaygentMessage, HaygentSession } from '../interfaces';
import { HaygentService } from '../services';

export const HAYGENT_QUERY_KEY = ['haygent'];
const HAYGENT_SESSIONS_QUERY_KEY = [...HAYGENT_QUERY_KEY, 'sessions'];
const HAYGENT_DRAFT_MESSAGES_QUERY_KEY = [...HAYGENT_QUERY_KEY, 'draft', 'messages'];
const haygentSessionMessagesQueryKey = (sessionId: string | null) =>
  sessionId ? [...HAYGENT_SESSIONS_QUERY_KEY, sessionId, 'messages'] : HAYGENT_DRAFT_MESSAGES_QUERY_KEY;

export const useHaygentSessionsQuery = (enabled = true) =>
  useQuery({
    queryKey: HAYGENT_SESSIONS_QUERY_KEY,
    queryFn: HaygentService.listSessions,
    enabled,
    refetchOnMount: 'always',
  });

export const useHaygentSessionMessagesQuery = (sessionId: string | null) =>
  useQuery<HaygentMessage[]>({
    queryKey: haygentSessionMessagesQueryKey(sessionId),
    queryFn: () => (sessionId ? HaygentService.getSessionMessages(sessionId) : Promise.resolve([])),
    enabled: Boolean(sessionId),
    initialData: sessionId ? undefined : [],
  });

export const cacheHaygentSessionMessages = (sessionId: string | null, messages: HaygentMessage[]) => {
  queryClient.setQueryData(haygentSessionMessagesQueryKey(sessionId), messages);
};

export const updateHaygentSessionMessages = (sessionId: string | null, update: (messages: HaygentMessage[]) => HaygentMessage[]) => {
  // setQueryData is synchronous; the callback transforms the current cache, not asynchronous work.
  queryClient.setQueryData<HaygentMessage[]>(haygentSessionMessagesQueryKey(sessionId), (messages = []) => update(messages));
};

export const updateHaygentSessionName = (sessionId: string, name: string) => {
  queryClient.setQueryData<HaygentSession[]>(HAYGENT_SESSIONS_QUERY_KEY, (sessions = []) =>
    sessions.map((session) => (session.session_id === sessionId ? { ...session, name } : session)),
  );
};

export const refreshHaygentSessions = () => queryClient.invalidateQueries({ queryKey: HAYGENT_SESSIONS_QUERY_KEY, exact: true });

export const useDeleteHaygentSessionMutation = () =>
  useMutation({
    mutationFn: HaygentService.deleteSession,
    onSuccess: (_data, sessionId) => {
      queryClient.removeQueries({ queryKey: haygentSessionMessagesQueryKey(sessionId) });
      refreshHaygentSessions();
    },
  });
